/**
 * Aids Page - Public Housing Aids
 * Information about available government aids for housing
 */

import { storage, requireAuth, requireProfile } from '../utils/storage.js';
import { renderNavbar } from '../components/navbar.js';
import { renderFooter } from '../components/footer.js';

// Ayudas por Comunidad Autónoma (datos simplificados para MVP)
const AYUDAS_DATA = {
  national: [
    {
      id: 'ico-aval',
      name: 'Aval ICO Primera Vivienda',
      type: 'compra',
      description: 'El Estado avala hasta el 20% de la hipoteca para jóvenes y familias que compran su primera vivienda.',
      requirements: ['Menores de 35 años (o familias con menores)', 'Primera vivienda habitual', 'Precio máximo según zona', 'Ingresos máximos según IPREM'],
      amount: 'Aval del 20% del préstamo (hasta 25% para familias)',
      deadline: 'Vigente hasta fin de fondos',
      url: 'https://www.ico.es/web/ico/avales-para-la-adquisicion-de-primera-vivienda'
    },
    {
      id: 'plan-vivienda',
      name: 'Plan Estatal de Vivienda 2022-2025',
      type: 'alquiler',
      description: 'Ayudas al alquiler de hasta 250€/mes para jóvenes entre 18 y 35 años.',
      requirements: ['Entre 18 y 35 años', 'Ingresos inferiores a 3 veces el IPREM', 'Alquiler máximo de 600€ (900€ en zonas tensionadas)'],
      amount: 'Hasta 250€/mes durante 2 años',
      deadline: 'Según convocatoria de cada CCAA',
      url: 'https://www.mivau.gob.es/vivienda'
    },
    {
      id: 'bono-joven',
      name: 'Bono Alquiler Joven',
      type: 'alquiler',
      description: 'Ayuda directa de 250€ mensuales para jóvenes que viven de alquiler.',
      requirements: ['Entre 18 y 35 años', 'Ingresos anuales inferiores a 23.725€', 'Contrato de alquiler vigente'],
      amount: '250€/mes (máximo 2 años)',
      deadline: 'Convocatorias autonómicas',
      url: 'https://www.mivau.gob.es/vivienda/bono-alquiler-joven'
    }
  ],
  regional: {
    'Madrid': [
      {
        id: 'madrid-compra',
        name: 'Ayuda Compra Primera Vivienda Madrid',
        type: 'compra',
        description: 'Subvención para la compra de primera vivienda en la Comunidad de Madrid.',
        requirements: ['Menores de 35 años', 'Empadronados en Madrid', 'Primera vivienda', 'Ingresos máximos 4,5 IPREM'],
        amount: 'Hasta 10.800€',
        url: 'https://www.comunidad.madrid/servicios/vivienda'
      }
    ],
    'Cataluña': [
      {
        id: 'cat-alquiler',
        name: 'Prestaciones para el pago del alquiler',
        type: 'alquiler',
        description: 'Ayudas al pago del alquiler para personas en situación de vulnerabilidad.',
        requirements: ['Residir en Cataluña', 'Destinar más del 30% de ingresos al alquiler', 'Ingresos limitados'],
        amount: 'Hasta 200€/mes',
        url: 'https://habitatge.gencat.cat'
      }
    ],
    'Andalucía': [
      {
        id: 'and-compra',
        name: 'Programa de Fomento de Vivienda Joven',
        type: 'compra',
        description: 'Ayudas para jóvenes andaluces que quieren comprar su primera vivienda.',
        requirements: ['Menores de 35 años', 'Empadronados en Andalucía', 'Primera vivienda'],
        amount: 'Hasta 10.000€',
        url: 'https://www.juntadeandalucia.es/temas/vivienda-consumo/vivienda/ayudas.html'
      }
    ],
    'Comunidad Valenciana': [
      {
        id: 'val-alquiler',
        name: 'Renta Valenciana de Vivienda',
        type: 'alquiler',
        description: 'Complemento para familias vulnerables que destinan más del 30% de ingresos al alquiler.',
        requirements: ['Residir en Comunidad Valenciana', 'Situación de vulnerabilidad económica'],
        amount: 'Variable según situación',
        url: 'https://habitatge.gva.es'
      }
    ],
    'País Vasco': [
      {
        id: 'pv-alquiler',
        name: 'Prestación Complementaria de Vivienda',
        type: 'alquiler',
        description: 'Ayuda mensual para cubrir gastos de alquiler.',
        requirements: ['Residir en Euskadi', 'Ingresos bajos', 'Vivienda alquilada'],
        amount: 'Hasta 320€/mes',
        url: 'https://www.etxebide.euskadi.eus'
      }
    ]
  }
};

export function renderAids(container) {
  if (!requireAuth()) return;
  if (!requireProfile()) return;

  const profile = storage.getProfile();
  const nationalAids = AYUDAS_DATA.national;
  const regionalAids = AYUDAS_DATA.regional[profile.region] || [];
  const isYoung = profile.age < 36;

  container.innerHTML = `
    ${renderNavbar()}
    
    <main class="page page-background" style="padding-top: 100px;">
      <div class="container">
        
        <!-- Header -->
        <div style="text-align: center; margin-bottom: 48px;">
          <span class="section-badge">Ayudas Públicas</span>
          <h1 style="margin-bottom: 12px;">Ayudas disponibles para ti</h1>
          <p style="color: var(--gray-500); max-width: 600px; margin: 0 auto;">
            Hemos seleccionado las ayudas más relevantes según tu perfil: 
            <strong>${profile.age} años</strong> en <strong>${profile.region}</strong>.
          </p>
        </div>
        
        <!-- Disclaimer -->
        <div class="alert alert-warning" style="margin-bottom: 32px;">
          <i data-feather="alert-triangle"></i>
          <div>
            <strong>⚠️ Información orientativa</strong>
            <p style="margin: 4px 0 0 0; font-size: 14px;">
              Los requisitos y plazos pueden variar. Consulta siempre las fuentes oficiales antes de solicitar cualquier ayuda. 
              Esta información se actualiza periódicamente pero puede no reflejar los últimos cambios legislativos.
            </p>
          </div>
        </div>
        
        <!-- Filters -->
        <div class="tabs" style="max-width: 400px; margin-bottom: 32px;">
          <button class="tab active" data-filter="all">Todas</button>
          <button class="tab" data-filter="compra">Compra</button>
          <button class="tab" data-filter="alquiler">Alquiler</button>
        </div>
        
        <!-- National Aids -->
        <div style="margin-bottom: 48px;">
          <h2 style="font-size: 20px; margin-bottom: 24px; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">🇪🇸</span>
            Ayudas estatales
          </h2>
          
          <div class="aids-grid" id="nationalAidsGrid">
            ${nationalAids.map(aid => renderAidCard(aid, isYoung)).join('')}
          </div>
        </div>
        
        <!-- Regional Aids -->
        <div style="margin-bottom: 48px;">
          <h2 style="font-size: 20px; margin-bottom: 24px; display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 24px;">📍</span>
            Ayudas en ${profile.region}
          </h2>
          
          <div class="aids-grid" id="regionalAidsGrid">
            ${regionalAids.length > 0
      ? regionalAids.map(aid => renderAidCard(aid, isYoung)).join('')
      : `
                <div class="card" style="grid-column: span 2; text-align: center; padding: 40px;">
                  <div style="font-size: 48px; margin-bottom: 16px;">🔍</div>
                  <h3 style="margin-bottom: 8px;">No hemos encontrado ayudas específicas</h3>
                  <p style="color: var(--gray-500); margin-bottom: 16px;">
                    No tenemos información detallada sobre ayudas en ${profile.region}. 
                    Te recomendamos consultar directamente con tu Comunidad Autónoma.
                  </p>
                  <a href="https://www.google.com/search?q=ayudas+vivienda+${encodeURIComponent(profile.region)}" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     class="btn btn-secondary">
                    <i data-feather="external-link"></i>
                    Buscar más información
                  </a>
                </div>
              `
    }
          </div>
        </div>
        
        <!-- Additional Resources -->
        <div class="card" style="background: var(--gray-50);">
          <h3 style="margin-bottom: 20px;">📚 Recursos adicionales</h3>
          
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 16px;">
            <a href="https://www.mivau.gob.es/vivienda" target="_blank" rel="noopener noreferrer" class="resource-link">
              <i data-feather="external-link"></i>
              <div>
                <div style="font-weight: 600;">Ministerio de Vivienda</div>
                <div style="font-size: 12px; color: var(--gray-500);">Portal oficial del gobierno</div>
              </div>
            </a>
            
            <a href="https://www.ico.es" target="_blank" rel="noopener noreferrer" class="resource-link">
              <i data-feather="external-link"></i>
              <div>
                <div style="font-weight: 600;">Instituto de Crédito Oficial</div>
                <div style="font-size: 12px; color: var(--gray-500);">Avales y financiación pública</div>
              </div>
            </a>
            
            <a href="https://www.boe.es" target="_blank" rel="noopener noreferrer" class="resource-link">
              <i data-feather="external-link"></i>
              <div>
                <div style="font-weight: 600;">BOE</div>
                <div style="font-size: 12px; color: var(--gray-500);">Boletín Oficial del Estado</div>
              </div>
            </a>
          </div>
        </div>
        
      </div>
    </main>
    
    ${renderFooter()}
    
    <style>
      .aids-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
        gap: 24px;
      }
      
      .aid-card {
        transition: var(--transition-base);
      }
      
      .aid-card.hidden {
        display: none;
      }
      
      .aid-badge {
        display: inline-flex;
        padding: 4px 12px;
        border-radius: var(--radius-full);
        font-size: 12px;
        font-weight: 600;
      }
      
      .aid-badge.compra {
        background: rgba(26, 82, 118, 0.1);
        color: var(--primary-600);
      }
      
      .aid-badge.alquiler {
        background: rgba(16, 185, 129, 0.1);
        color: var(--success);
      }
      
      .resource-link {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
        background: white;
        border-radius: var(--radius-lg);
        transition: var(--transition-fast);
        text-decoration: none;
        color: inherit;
      }
      
      .resource-link:hover {
        box-shadow: var(--shadow-md);
        transform: translateY(-2px);
      }
      
      .resource-link svg {
        color: var(--primary-600);
        flex-shrink: 0;
      }
      
      @media (max-width: 768px) {
        .aids-grid {
          grid-template-columns: 1fr;
        }
      }

      .page-background {
        background: 
          radial-gradient(circle at 20px 20px, var(--primary-100) 2px, transparent 0),
          linear-gradient(135deg, var(--primary-50) 0%, white 100%);
        background-size: 40px 40px, 100% 100%;
        background-attachment: fixed;
      }
    </style>
  `;

  initAidsFilters();
}

function renderAidCard(aid, isYoung) {
  const isRelevant = aid.id === 'ico-aval' && isYoung;

  return `
    <div class="card aid-card" data-type="${aid.type}">
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px;">
        <div>
          <span class="aid-badge ${aid.type}">${aid.type === 'compra' ? '🏠 Compra' : '🔑 Alquiler'}</span>
          ${isRelevant ? '<span class="badge badge-primary" style="margin-left: 8px;">Recomendada</span>' : ''}
        </div>
      </div>
      
      <h3 style="font-size: 18px; margin-bottom: 8px;">${aid.name}</h3>
      <p style="color: var(--gray-600); font-size: 14px; margin-bottom: 16px; line-height: 1.6;">
        ${aid.description}
      </p>
      
      <div style="margin-bottom: 16px;">
        <div style="font-weight: 600; font-size: 14px; margin-bottom: 8px;">Requisitos:</div>
        <ul style="font-size: 13px; color: var(--gray-600); margin-left: 16px;">
          ${aid.requirements.map(req => `<li style="margin-bottom: 4px;">${req}</li>`).join('')}
        </ul>
      </div>
      
      <div style="padding: 12px; background: var(--primary-50); border-radius: var(--radius-lg); margin-bottom: 16px;">
        <div style="font-size: 12px; color: var(--primary-600); margin-bottom: 2px;">Cuantía</div>
        <div style="font-weight: 700; color: var(--primary-700);">${aid.amount}</div>
      </div>
      
      ${aid.deadline ? `
        <div style="font-size: 13px; color: var(--gray-500); margin-bottom: 16px;">
          <i data-feather="clock" style="width: 14px; height: 14px; margin-right: 4px;"></i>
          ${aid.deadline}
        </div>
      ` : ''}
      
      <a href="${aid.url}" target="_blank" rel="noopener noreferrer" class="btn btn-secondary w-full">
        <i data-feather="external-link"></i>
        Más información
      </a>
    </div>
  `;
}

function initAidsFilters() {
  const tabs = document.querySelectorAll('.tab');
  const nationalCards = document.querySelectorAll('#nationalAidsGrid .aid-card');
  const regionalCards = document.querySelectorAll('#regionalAidsGrid .aid-card');

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      // Update active tab
      tabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');

      const filter = tab.dataset.filter;

      // Filter cards
      [...nationalCards, ...regionalCards].forEach(card => {
        if (filter === 'all' || card.dataset.type === filter) {
          card.classList.remove('hidden');
        } else {
          card.classList.add('hidden');
        }
      });
    });
  });
}
